import { useEffect, useId, useState } from "react";
import {
  submissionsService,
  type ContactMatchStatus,
  type EntryContactHistoryItem,
} from "@/api/submissions.service";
import { formatDateTimeShort } from "@/utils/dateFormatter";
import { CONTACT_LINK_STATE_LABELS, getContactLinkState } from "./contactMatch";
import styles from "./LinkSubmissionContactModal.module.css";

type HistoryState =
  | { status: "loading" }
  | { status: "ready"; history: EntryContactHistoryItem[] }
  | { status: "error" };

type ContactHistoryProps = {
  submissionId: string;
  entryId: string;
  refreshKey: number;
};

function statusLabel(item: EntryContactHistoryItem): string {
  const state = getContactLinkState({
    contactMatchStatus: item.status as ContactMatchStatus,
    contactId: item.contactId,
  });
  return state ? CONTACT_LINK_STATE_LABELS[state] : item.status;
}

function withContact(label: string, contactName: string | null): string {
  return contactName ? `${label}, ${contactName}` : label;
}

function describeHistoryAction(item: EntryContactHistoryItem): string {
  const contact = item.contactName ?? "an unknown contact";
  const previous = item.previousContactName ?? "an unknown contact";
  switch (item.action) {
    case "link":
      return `Linked to ${contact}`;
    case "relink":
      return `Changed from ${previous} to ${contact}`;
    case "unlink":
      return `Unlinked ${previous}`;
    case "no_contact":
      return "Marked as no matching contact";
    case "rematch":
      return `Re-ran auto-match → ${withContact(statusLabel(item), item.contactName)}`;
    case "auto_match":
      return `Auto-match → ${withContact(statusLabel(item), item.contactName)}`;
    case "firm_selected":
      return `Firm selected ${contact}`;
    default:
      return item.action;
  }
}

function describeHistoryActor(item: EntryContactHistoryItem): string {
  if (item.actorType === "system") return "System (auto-match)";
  const email = item.actorEmail ?? "Unknown user";
  return item.action === "firm_selected" ? `${email} (firm)` : email;
}

export function ContactHistory({ submissionId, entryId, refreshKey }: ContactHistoryProps) {
  const [expanded, setExpanded] = useState(false);
  const [state, setState] = useState<HistoryState>({ status: "loading" });
  const headingId = useId();
  const panelId = useId();

  useEffect(() => {
    if (!expanded || !submissionId || !entryId) return;
    let cancelled = false;
    setState({ status: "loading" });
    submissionsService
      .getEntryContactHistory(submissionId, entryId)
      .then(response => {
        if (!cancelled) setState({ status: "ready", history: response.history });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, submissionId, entryId, refreshKey]);

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <h3 id={headingId} className={styles.sectionTitle}>
        <button
          type="button"
          className={styles.disclosure}
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => setExpanded(open => !open)}
        >
          <span className={styles.disclosureIcon} aria-hidden="true">
            {expanded ? "▾" : "▸"}
          </span>
          History
        </button>
      </h3>
      {expanded && (
        <div id={panelId} className={styles.history} aria-live="polite">
          {state.status === "loading" && (
            <p className={styles.loading} role="status">
              <span className={styles.spinner} aria-hidden="true" />
              Loading history...
            </p>
          )}
          {state.status === "error" && (
            <p className={styles.error} role="alert">
              Couldn't load the history. Close and try again.
            </p>
          )}
          {state.status === "ready" && state.history.length === 0 && (
            <p className={styles.hint}>No changes recorded yet.</p>
          )}
          {state.status === "ready" && state.history.length > 0 && (
            <ol className={styles.historyList}>
              {state.history.map(item => (
                <li key={item.id} className={styles.historyItem}>
                  <time className={styles.historyTime} dateTime={item.createdAt}>
                    {formatDateTimeShort(item.createdAt)}
                  </time>
                  <span className={styles.historyActor}>{describeHistoryActor(item)}</span>
                  <span className={styles.historyAction}>{describeHistoryAction(item)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}
